"use client";

import { AnimatePresence } from "motion/react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import { useCart } from "@/components/cart/cart-provider";
import { CartDestinationField } from "@/components/cart/cart-destination-field";
import { CartLineItem } from "@/components/cart/cart-line-item";
import { CartSummary } from "@/components/cart/cart-summary";
import { CartWhatsAppOrderActions } from "@/components/cart/cart-whatsapp-order-actions";
import { CartWholesaleNotice } from "@/components/cart/cart-wholesale-notice";
import { CustomerPhoneField } from "@/components/cart/customer-phone-field";
import { trackCatalogEvent } from "@/components/catalog/analytics";
import { MotionList, MotionListItem, MotionSection } from "@/components/ui/motion-primitives";
import { catalogUiContent } from "@/data/catalog-ui-content";

export function CartPage() {
  const {
    isHydrated,
    items,
    removeItem,
    setShippingDestination,
    shippingDestination,
    storageError,
    totals,
    updateQuantity,
    wholesaleProgress,
  } = useCart();
  const [customerPhone, setCustomerPhone] = useState("");
  const [phoneShakeSignal, setPhoneShakeSignal] = useState(0);
  const hasTrackedView = useRef(false);

  useEffect(() => {
    if (!isHydrated || hasTrackedView.current) {
      return;
    }

    hasTrackedView.current = true;
    trackCatalogEvent({
      eventName: "cart_view",
      payload: {
        itemCount: totals.itemCount,
        productCount: items.length,
      },
    });
  }, [isHydrated, items.length, totals.itemCount]);

  return (
    <main className="mx-auto w-full max-w-md px-4 pb-32 sm:max-w-3xl md:max-w-5xl lg:max-w-6xl">
      <MotionSection className="mt-2">
        <h1 className="text-2xl font-extrabold text-primary">{catalogUiContent.cartTitle}</h1>
        <p className="text-sm text-muted">{items.length} productos</p>
      </MotionSection>

      {storageError ? (
        <MotionSection className="mt-4 rounded-lg border border-accent-orange bg-surface p-3 text-sm font-bold text-accent-orange">
          {storageError}
        </MotionSection>
      ) : null}

      {items.length > 0 ? (
        <div className="mt-6 md:grid md:grid-cols-3 md:gap-6 md:items-start">
          {/* Columna Izquierda: Aviso mayorista e Items del Carrito */}
          <div className="md:col-span-2">
            {wholesaleProgress ? (
              <MotionSection className="mb-3">
                <CartWholesaleNotice
                  itemCount={totals.itemCount}
                  progress={wholesaleProgress}
                />
              </MotionSection>
            ) : null}
            <MotionList className="space-y-3">
              <AnimatePresence initial={false}>
                {items.map((item) => (
                  <MotionListItem key={item.productId} layout="position">
                    <CartLineItem
                      item={item}
                      isWholesaleOrder={totals.isWholesaleOrder}
                      onQuantityChange={(quantity) => {
                        updateQuantity(item.productId, quantity);
                        trackCatalogEvent({
                          eventName: "cart_update_quantity",
                          productId: item.productId,
                          sku: item.sku,
                          payload: {
                            availability: item.availability,
                            familyName: item.familyName,
                            productType: item.type,
                            quantity,
                          },
                        });
                      }}
                      onRemove={() => {
                        removeItem(item.productId);
                        trackCatalogEvent({
                          eventName: "cart_remove_item",
                          productId: item.productId,
                          payload: {
                            availability: item.availability,
                            familyName: item.familyName,
                            productType: item.type,
                            quantity: item.quantity,
                          },
                          sku: item.sku,
                        });
                      }}
                    />
                  </MotionListItem>
                ))}
              </AnimatePresence>
            </MotionList>
          </div>

          {/* Columna Derecha: Resumen y Datos Para el Envío */}
          <div className="mt-6 md:mt-0 md:col-span-1 md:sticky md:top-20">
            <div className="flex flex-col gap-4">
              <MotionSection>
                <CartSummary totals={totals} />
              </MotionSection>
              <MotionSection>
                <section className="rounded-xl border border-outline-variant bg-surface p-4 shadow-sm">
                  <h2 className="text-xl font-extrabold text-primary">Datos para el envío</h2>
                  <div className="mt-3 flex flex-col gap-4">
                    <CustomerPhoneField
                      onChange={setCustomerPhone}
                      shakeSignal={phoneShakeSignal}
                      value={customerPhone}
                    />
                    <CartDestinationField
                      onChange={setShippingDestination}
                      value={shippingDestination}
                    />
                    <CartWhatsAppOrderActions
                      customerPhone={customerPhone}
                      onPhoneInvalid={() => setPhoneShakeSignal((signal) => signal + 1)}
                    />
                  </div>
                </section>
              </MotionSection>
            </div>
          </div>
        </div>
      ) : (
        <MotionSection className="mt-6 rounded-xl border border-outline-variant bg-surface p-6">
          <h2 className="text-lg font-extrabold text-primary">
            {catalogUiContent.cartEmptyTitle}
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted">{catalogUiContent.cartEmptyBody}</p>
          <Link
            className="mt-4 inline-flex min-h-11 items-center rounded-lg bg-primary px-4 text-sm font-extrabold text-white outline-none transition-colors hover:bg-primary-container focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            href="/catalogo"
            prefetch={false}
          >
            Ver catálogo
          </Link>
        </MotionSection>
      )}
    </main>
  );
}
